/**
 * Localized UI strings for shared components (#1772).
 *
 * Shared components render in the editor AND in the generator/bollardbuddy
 * islands, and only the editor mounts a react-intl IntlProvider — so, like the
 * number/price helpers in `@shared/utils/format`, these strings are resolved
 * framework-free with a small curated table keyed by locale (the same approach
 * as PERIOD_SUFFIX there). The active locale comes from the same `locale`
 * localStorage key the editor persists, falling back to the browser language.
 *
 * These strings are NOT part of the formatjs extraction pipeline (that scans
 * for FormattedMessage/defineMessages and feeds the editor-only catalogs), so
 * translations here are hand-maintained. Keep terminology in sync with the
 * editor catalogs in src/editor/i18n/locales/ (assets: es/pt "recursos",
 * fr "actifs"; geo tokens: es "geotokens", pt "tokens geo", fr "jetons géo").
 *
 * Usage in React components (live-updates when the editor switches language):
 *   const t = useSharedMessages();
 *   <span>{t('openProfile')}</span>
 *
 * Usage in plain modules (resolved per call, e.g. pricing getters):
 *   formatSharedMessage('billedYearly', { total: '$84', period: '/year' })
 */

import { useCallback, useSyncExternalStore } from 'react';
import { getActiveLocale } from '../utils/format';
import { DEFAULT_LOCALE } from './locales';

const SHARED_MESSAGES = {
  // Billing cycle detail (UpgradeModal pricing)
  billedMonthly: {
    en: 'billed monthly',
    es: 'facturado mensualmente',
    'pt-BR': 'cobrado mensalmente',
    fr: 'facturé mensuellement'
  },
  billedYearly: {
    en: 'billed yearly, {total}{period}',
    es: 'facturado anualmente, {total}{period}',
    'pt-BR': 'cobrado anualmente, {total}{period}',
    fr: 'facturé annuellement, {total}{period}'
  },

  // Profile menu (shared dropdown used in the generator + Bollard Buddy)
  manageAccount: {
    en: 'Manage Account',
    es: 'Administrar cuenta',
    'pt-BR': 'Gerenciar conta',
    fr: 'Gérer le compte'
  },
  logOut: {
    en: 'Log Out',
    es: 'Cerrar sesión',
    'pt-BR': 'Sair',
    fr: 'Se déconnecter'
  },
  loadingUsername: {
    en: 'Loading username…',
    es: 'Cargando nombre de usuario…',
    'pt-BR': 'Carregando nome de usuário…',
    fr: "Chargement du nom d'utilisateur…"
  },
  notSignedIn: {
    en: 'Not signed in',
    es: 'Sesión no iniciada',
    'pt-BR': 'Não conectado',
    fr: 'Non connecté'
  },
  signInOrCreateAccount: {
    en: 'Sign in or create account',
    es: 'Inicia sesión o crea una cuenta',
    'pt-BR': 'Entrar ou criar conta',
    fr: 'Se connecter ou créer un compte'
  },
  language: {
    en: 'Language',
    es: 'Idioma',
    'pt-BR': 'Idioma',
    fr: 'Langue'
  },
  // Shown in the confirm-before-reload dialog when switching language in an app
  // that reloads to apply it (the generator). {from}/{to} are language endonyms
  // and are never translated. The dialog renders this in BOTH the current and
  // the requested language.
  reloadLanguageConfirm: {
    en: 'Reload to switch language from {from} to {to}?',
    es: '¿Recargar para cambiar el idioma de {from} a {to}?',
    'pt-BR': 'Recarregar para mudar o idioma de {from} para {to}?',
    fr: 'Recharger pour changer la langue de {from} à {to} ?'
  },
  cancel: {
    en: 'Cancel',
    es: 'Cancelar',
    'pt-BR': 'Cancelar',
    fr: 'Annuler'
  },
  reload: {
    en: 'Reload',
    es: 'Recargar',
    'pt-BR': 'Recarregar',
    fr: 'Recharger'
  },

  // ProfileButton tooltips
  openProfile: {
    en: 'Open profile',
    es: 'Abrir perfil',
    'pt-BR': 'Abrir perfil',
    fr: 'Ouvrir le profil'
  },
  signIn: {
    en: 'Sign in',
    es: 'Iniciar sesión',
    'pt-BR': 'Fazer login',
    fr: 'Se connecter'
  },

  // Token labels + details hover card
  geoTokens: {
    en: 'Geo Tokens',
    es: 'Geotokens',
    'pt-BR': 'Tokens Geo',
    fr: 'Jetons géo'
  },
  aiGenerationTokens: {
    en: 'AI Generation Tokens',
    es: 'Tokens de generación IA',
    'pt-BR': 'Tokens de geração de IA',
    fr: 'Jetons de génération IA'
  },
  geoTokensDescription: {
    en: 'Used for geospatial features like 3D map tiles and location services.',
    es: 'Se usan para funciones geoespaciales como mosaicos de mapas 3D y servicios de ubicación.',
    'pt-BR':
      'Usados para recursos geoespaciais como blocos de mapa 3D e serviços de localização.',
    fr: 'Utilisés pour les fonctions géospatiales comme les tuiles de carte 3D et les services de localisation.'
  },
  genTokensDescription: {
    en: 'Used for AI-powered image and video generation.',
    es: 'Se usan para la generación de imágenes y videos con IA.',
    'pt-BR': 'Usados para geração de imagens e vídeos com IA.',
    fr: "Utilisés pour la génération d'images et de vidéos par IA."
  },
  currentBalance: {
    en: 'Current Balance:',
    es: 'Saldo actual:',
    'pt-BR': 'Saldo atual:',
    fr: 'Solde actuel :'
  },
  outOfTokensWarning: {
    en: 'You are out of {tokenLabel}!',
    es: '¡Te quedaste sin {tokenLabel}!',
    'pt-BR': 'Você ficou sem {tokenLabel}!',
    fr: "Vous n'avez plus de {tokenLabel} !"
  },
  lowTokensWarning: {
    en: 'You are running low on {tokenLabel}!',
    es: '¡Te quedan pocos {tokenLabel}!',
    'pt-BR': 'Seus {tokenLabel} estão acabando!',
    fr: 'Il ne vous reste presque plus de {tokenLabel} !'
  },
  getMoreTokens: {
    en: 'Get More Tokens',
    es: 'Obtener más tokens',
    'pt-BR': 'Obter mais tokens',
    fr: 'Obtenir plus de jetons'
  },
  tokenUsage: {
    en: 'Token Usage:',
    es: 'Uso de tokens:',
    'pt-BR': 'Uso de tokens:',
    fr: 'Utilisation des jetons :'
  },
  tipMapTile: {
    en: '1 token = 1 map tile request',
    es: '1 token = 1 solicitud de mosaico de mapa',
    'pt-BR': '1 token = 1 solicitação de bloco de mapa',
    fr: '1 jeton = 1 requête de tuile de carte'
  },
  tipLocationServices: {
    en: 'Location services use tokens',
    es: 'Los servicios de ubicación usan tokens',
    'pt-BR': 'Serviços de localização usam tokens',
    fr: 'Les services de localisation utilisent des jetons'
  },
  tipGeospatialFeatures: {
    en: 'Geospatial features require tokens',
    es: 'Las funciones geoespaciales requieren tokens',
    'pt-BR': 'Recursos geoespaciais exigem tokens',
    fr: 'Les fonctions géospatiales nécessitent des jetons'
  },

  // BuyTokensModal — one-time token pack purchases (#1374). Pack names
  // (Starter/Standard/Power) are deliberately untranslated, same as the
  // Pro/Max tier names.
  buyTokensTitle: {
    en: 'Buy More Tokens',
    es: 'Comprar más tokens',
    'pt-BR': 'Comprar mais tokens',
    fr: 'Acheter plus de jetons'
  },
  buyTokensSubtitle: {
    en: 'One-time token packs for your AI generations.',
    es: 'Paquetes de tokens de pago único para tus generaciones con IA.',
    'pt-BR': 'Pacotes de tokens de compra única para suas gerações com IA.',
    fr: 'Des packs de jetons à achat unique pour vos générations IA.'
  },
  buyTokensSignInPrompt: {
    en: 'Sign in to purchase tokens.',
    es: 'Inicia sesión para comprar tokens.',
    'pt-BR': 'Faça login para comprar tokens.',
    fr: 'Connectez-vous pour acheter des jetons.'
  },
  signInToCloud: {
    en: 'Sign in to 3DStreet Cloud',
    es: 'Iniciar sesión en 3DStreet Cloud',
    'pt-BR': 'Entrar no 3DStreet Cloud',
    fr: 'Se connecter à 3DStreet Cloud'
  },
  buyTokensPaidPlanOnly: {
    en: 'Token packs are available on Pro and Max plans. Upgrade to get a monthly token allowance plus the option to buy more anytime.',
    es: 'Los paquetes de tokens están disponibles en los planes Pro y Max. Mejora tu plan para obtener una asignación mensual de tokens y la opción de comprar más en cualquier momento.',
    'pt-BR':
      'Os pacotes de tokens estão disponíveis nos planos Pro e Max. Faça upgrade para receber uma cota mensal de tokens e a opção de comprar mais a qualquer momento.',
    fr: "Les packs de jetons sont disponibles avec les forfaits Pro et Max. Passez à un forfait supérieur pour obtenir une allocation mensuelle de jetons et la possibilité d'en acheter davantage à tout moment."
  },
  upgradeToPro: {
    en: 'Upgrade to Pro',
    es: 'Mejorar a Pro',
    'pt-BR': 'Fazer upgrade para o Pro',
    fr: 'Passer à Pro'
  },
  buyTokensOneTime: {
    en: 'one-time',
    es: 'pago único',
    'pt-BR': 'pagamento único',
    fr: 'paiement unique'
  },
  buyTokensPackTokens: {
    en: '{tokens} AI tokens',
    es: '{tokens} tokens de IA',
    'pt-BR': '{tokens} tokens de IA',
    fr: '{tokens} jetons IA'
  },
  buyTokensBuyCta: {
    en: 'Buy {name}',
    es: 'Comprar {name}',
    'pt-BR': 'Comprar {name}',
    fr: 'Acheter {name}'
  },
  buy: {
    en: 'Buy',
    es: 'Comprar',
    'pt-BR': 'Comprar',
    fr: 'Acheter'
  },
  buyTokensUnavailable: {
    en: 'Token packs are not available yet on this deployment. Please check back soon.',
    es: 'Los paquetes de tokens aún no están disponibles en esta versión. Vuelve pronto.',
    'pt-BR':
      'Os pacotes de tokens ainda não estão disponíveis nesta versão. Volte em breve.',
    fr: 'Les packs de jetons ne sont pas encore disponibles sur ce déploiement. Revenez bientôt.'
  },
  buyTokensCheckoutTitle: {
    en: 'Complete your purchase',
    es: 'Completa tu compra',
    'pt-BR': 'Conclua sua compra',
    fr: 'Finalisez votre achat'
  },
  buyTokensSuccessTitle: {
    en: 'Tokens Added!',
    es: '¡Tokens añadidos!',
    'pt-BR': 'Tokens adicionados!',
    fr: 'Jetons ajoutés !'
  },
  buyTokensSuccessMessage: {
    en: '{tokens} tokens are now on your account.',
    es: '{tokens} tokens ya están en tu cuenta.',
    'pt-BR': '{tokens} tokens já estão na sua conta.',
    fr: '{tokens} jetons ont été ajoutés à votre compte.'
  },
  // ProjectPassModal — one-time time-boxed Pro pass (#1922), opened by the
  // #project-pass deep link. `projectPassName` is the product's display name:
  // rename the pass here only.
  projectPassName: {
    en: 'Project Pass',
    es: 'Pase de Proyecto',
    'pt-BR': 'Passe de Projeto',
    fr: 'Pass Projet'
  },
  projectPassSubtitle: {
    en: '{months} months of 3DStreet Pro for one payment. No subscription, no auto-renewal.',
    es: '{months} meses de 3DStreet Pro con un solo pago. Sin suscripción ni renovación automática.',
    'pt-BR':
      '{months} meses de 3DStreet Pro com um único pagamento. Sem assinatura e sem renovação automática.',
    fr: '{months} mois de 3DStreet Pro en un seul paiement. Sans abonnement ni renouvellement automatique.'
  },
  projectPassFeatPro: {
    en: 'All Pro features for {days} days',
    es: 'Todas las funciones Pro durante {days} días',
    'pt-BR': 'Todos os recursos Pro por {days} dias',
    fr: 'Toutes les fonctionnalités Pro pendant {days} jours'
  },
  projectPassFeatTokens: {
    en: '{tokens} AI tokens up front, yours to keep',
    es: '{tokens} tokens de IA por adelantado, sin vencimiento',
    'pt-BR': '{tokens} tokens de IA antecipados, que não expiram',
    fr: "{tokens} jetons IA d'emblée, qui n'expirent pas"
  },
  projectPassFeatExports: {
    en: 'Watermark-free downloads, unlimited geospatial maps and custom model uploads',
    es: 'Descargas sin marca de agua, mapas geoespaciales ilimitados y carga de modelos personalizados',
    'pt-BR':
      "Downloads sem marca d'água, mapas geoespaciais ilimitados e upload de modelos personalizados",
    fr: 'Téléchargements sans filigrane, cartes géospatiales illimitées et import de modèles personnalisés'
  },
  projectPassFeatKeep: {
    en: 'When it ends you return to the free plan. Your scenes and files stay.',
    es: 'Al terminar vuelves al plan gratuito. Tus escenas y archivos se conservan.',
    'pt-BR':
      'Quando terminar, você volta ao plano gratuito. Suas cenas e arquivos continuam salvos.',
    fr: 'À la fin, vous revenez au forfait gratuit. Vos scènes et fichiers sont conservés.'
  },
  projectPassSignInPrompt: {
    en: 'Sign in or create a free account to buy the pass.',
    es: 'Inicia sesión o crea una cuenta gratuita para comprar el pase.',
    'pt-BR': 'Entre ou crie uma conta gratuita para comprar o passe.',
    fr: 'Connectez-vous ou créez un compte gratuit pour acheter le pass.'
  },
  projectPassActiveLead: {
    en: 'You have an active pass.',
    es: 'Tienes un pase activo.',
    'pt-BR': 'Você tem um passe ativo.',
    fr: 'Vous avez un pass actif.'
  },
  projectPassActiveUntil: {
    en: 'It runs until {date}. Buying another adds {days} days.',
    es: 'Dura hasta el {date}. Si compras otro, se suman {days} días.',
    'pt-BR': 'Ele vale até {date}. Comprar outro adiciona {days} dias.',
    fr: "Il est valable jusqu'au {date}. En acheter un autre ajoute {days} jours."
  },
  projectPassSubscriberNote: {
    en: 'You already have Pro through your subscription. This pass adds {tokens} AI tokens now, and keeps Pro for {days} days if you cancel.',
    es: 'Ya tienes Pro con tu suscripción. Este pase añade {tokens} tokens de IA ahora y mantiene Pro durante {days} días si cancelas.',
    'pt-BR':
      'Você já tem o Pro pela sua assinatura. Este passe adiciona {tokens} tokens de IA agora e mantém o Pro por {days} dias se você cancelar.',
    fr: 'Vous avez déjà Pro avec votre abonnement. Ce pass ajoute {tokens} jetons IA maintenant et garde Pro pendant {days} jours si vous résiliez.'
  },
  projectPassTeamNote: {
    en: 'You already have Pro through your organization ({domain}). A pass would only add {tokens} AI tokens.',
    es: 'Ya tienes Pro a través de tu organización ({domain}). Un pase solo añadiría {tokens} tokens de IA.',
    'pt-BR':
      'Você já tem o Pro pela sua organização ({domain}). Um passe só adicionaria {tokens} tokens de IA.',
    fr: 'Vous avez déjà Pro grâce à votre organisation ({domain}). Un pass ajouterait seulement {tokens} jetons IA.'
  },
  projectPassSeeTokenPacks: {
    en: 'Just need tokens? See token packs',
    es: '¿Solo necesitas tokens? Ver paquetes de tokens',
    'pt-BR': 'Só precisa de tokens? Ver pacotes de tokens',
    fr: 'Besoin de jetons uniquement ? Voir les packs de jetons'
  },
  projectPassBuyCta: {
    en: 'Buy {name}',
    es: 'Comprar {name}',
    'pt-BR': 'Comprar {name}',
    fr: 'Acheter {name}'
  },
  projectPassUnavailable: {
    en: 'The pass is not available yet on this deployment. Please check back soon.',
    es: 'El pase aún no está disponible en esta versión. Vuelve pronto.',
    'pt-BR': 'O passe ainda não está disponível nesta versão. Volte em breve.',
    fr: "Le pass n'est pas encore disponible sur ce déploiement. Revenez bientôt."
  },
  projectPassSuccessTitle: {
    en: 'Pro is unlocked!',
    es: '¡Pro está desbloqueado!',
    'pt-BR': 'O Pro está desbloqueado!',
    fr: 'Pro est débloqué !'
  },
  projectPassSuccessMessage: {
    en: 'You have Pro until {date}, and {tokens} AI tokens were added to your account.',
    es: 'Tienes Pro hasta el {date} y se añadieron {tokens} tokens de IA a tu cuenta.',
    'pt-BR':
      'Você tem o Pro até {date}, e {tokens} tokens de IA foram adicionados à sua conta.',
    fr: "Vous avez Pro jusqu'au {date}, et {tokens} jetons IA ont été ajoutés à votre compte."
  },
  // RedeemPassModal — redeem a Project Pass code someone else paid for
  // (#1922 follow-up), opened by the #redeem?code= deep link.
  redeemPassTitle: {
    en: 'Redeem your {name}',
    es: 'Canjea tu {name}',
    'pt-BR': 'Resgate seu {name}',
    fr: 'Utilisez votre {name}'
  },
  redeemPassSubtitle: {
    en: 'Your code unlocks {days} days of 3DStreet Pro and {tokens} AI tokens. Your days start when you redeem.',
    es: 'Tu código desbloquea {days} días de 3DStreet Pro y {tokens} tokens de IA. Los días empiezan a contar cuando lo canjeas.',
    'pt-BR':
      'Seu código desbloqueia {days} dias de 3DStreet Pro e {tokens} tokens de IA. Os dias começam a contar quando você resgata.',
    fr: "Votre code débloque {days} jours de 3DStreet Pro et {tokens} jetons IA. Les jours commencent à courir quand vous l'utilisez."
  },
  redeemPassSignInPrompt: {
    en: 'Sign in or create a free account to redeem your code.',
    es: 'Inicia sesión o crea una cuenta gratuita para canjear tu código.',
    'pt-BR': 'Entre ou crie uma conta gratuita para resgatar seu código.',
    fr: 'Connectez-vous ou créez un compte gratuit pour utiliser votre code.'
  },
  redeemPassCodeLabel: {
    en: 'Code',
    es: 'Código',
    'pt-BR': 'Código',
    fr: 'Code'
  },
  redeemPassCta: {
    en: 'Redeem',
    es: 'Canjear',
    'pt-BR': 'Resgatar',
    fr: 'Valider'
  },
  redeemPassRedeeming: {
    en: 'Redeeming…',
    es: 'Canjeando…',
    'pt-BR': 'Resgatando…',
    fr: 'Validation…'
  },
  redeemPassFromLine: {
    en: '{from} sent you this pass.',
    es: '{from} te envió este pase.',
    'pt-BR': '{from} enviou este passe para você.',
    fr: '{from} vous a envoyé ce pass.'
  },
  redeemPassAlreadyRedeemedTitle: {
    en: 'Already redeemed',
    es: 'Ya canjeado',
    'pt-BR': 'Já resgatado',
    fr: 'Déjà utilisé'
  },
  redeemPassAlreadyRedeemed: {
    en: 'You already redeemed this code, so nothing has changed. You have Pro until {date}.',
    es: 'Ya canjeaste este código, así que no ha cambiado nada. Tienes Pro hasta el {date}.',
    'pt-BR':
      'Você já resgatou este código, então nada mudou. Você tem o Pro até {date}.',
    fr: "Vous avez déjà utilisé ce code, rien n'a donc changé. Vous avez Pro jusqu'au {date}."
  },
  redeemPassErrorInvalid: {
    en: 'That code is not valid. Check it and try again.',
    es: 'Ese código no es válido. Revísalo e inténtalo de nuevo.',
    'pt-BR': 'Esse código não é válido. Confira e tente novamente.',
    fr: "Ce code n'est pas valide. Vérifiez-le et réessayez."
  },
  redeemPassErrorExpired: {
    en: 'That code has expired or is no longer active.',
    es: 'Ese código venció o ya no está activo.',
    'pt-BR': 'Esse código expirou ou não está mais ativo.',
    fr: "Ce code a expiré ou n'est plus actif."
  },
  redeemPassErrorExhausted: {
    en: 'That code has already been used the maximum number of times. Ask whoever shared it with you.',
    es: 'Ese código ya alcanzó su número máximo de usos. Consulta a quien te lo compartió.',
    'pt-BR':
      'Esse código já atingiu o número máximo de usos. Fale com quem o compartilhou com você.',
    fr: "Ce code a déjà été utilisé le nombre maximal de fois. Contactez la personne qui vous l'a transmis."
  },
  redeemPassErrorAlreadyPro: {
    en: "You already have 3DStreet Pro, so you don't need this pass. Please pass the code on to someone who doesn't have Pro.",
    es: 'Ya tienes 3DStreet Pro, así que no necesitas este pase. Comparte el código con alguien que no tenga Pro.',
    'pt-BR':
      'Você já tem o 3DStreet Pro, então não precisa deste passe. Compartilhe o código com alguém que não tenha o Pro.',
    fr: "Vous avez déjà 3DStreet Pro, vous n'avez donc pas besoin de ce pass. Transmettez le code à quelqu'un qui n'a pas Pro."
  },
  redeemPassErrorGeneric: {
    en: 'Something went wrong redeeming your code. Please try again.',
    es: 'Algo salió mal al canjear tu código. Inténtalo de nuevo.',
    'pt-BR': 'Algo deu errado ao resgatar seu código. Tente novamente.',
    fr: "Une erreur s'est produite lors de la validation de votre code. Veuillez réessayer."
  },
  back: {
    en: 'Back',
    es: 'Atrás',
    'pt-BR': 'Voltar',
    fr: 'Retour'
  },
  close: {
    en: 'Close',
    es: 'Cerrar',
    'pt-BR': 'Fechar',
    fr: 'Fermer'
  },
  done: {
    en: 'Done',
    es: 'Listo',
    'pt-BR': 'Concluído',
    fr: 'Terminé'
  },

  // Plan feature lines — shared pool referenced by id from UpgradeModal's
  // default list and the paywallSurfaces registry (featureIds arrays).
  featTokensMonthly: {
    en: '{tokens} AI generation tokens / month',
    es: '{tokens} tokens de generación IA al mes',
    'pt-BR': '{tokens} tokens de geração de IA por mês',
    fr: '{tokens} jetons de génération IA par mois'
  },
  featWatermark: {
    en: 'Download JPEG snapshots without watermark',
    es: 'Descarga instantáneas JPEG sin marca de agua',
    'pt-BR': "Baixe instantâneos JPEG sem marca d'água",
    fr: 'Téléchargez des instantanés JPEG sans filigrane'
  },
  featGeoUnlimited: {
    en: 'Unlimited geospatial maps & location changes',
    es: 'Mapas geoespaciales y cambios de ubicación ilimitados',
    'pt-BR': 'Mapas geoespaciais e mudanças de localização ilimitados',
    fr: 'Cartes géospatiales et changements de lieu illimités'
  },
  featHdRenders: {
    en: 'HD renders, AR-ready glTF & video export',
    es: 'Renders HD, glTF listo para RA y exportación de video',
    'pt-BR': 'Renders em HD, glTF pronto para RA e exportação de vídeo',
    fr: 'Rendus HD, glTF prêt pour la RA et export vidéo'
  },
  featCustomModels: {
    en: 'Import custom 3D models & SVG / glTF files',
    es: 'Importa modelos 3D personalizados y archivos SVG / glTF',
    'pt-BR': 'Importe modelos 3D personalizados e arquivos SVG / glTF',
    fr: 'Importez des modèles 3D personnalisés et des fichiers SVG / glTF'
  },
  featGlbExport: {
    en: 'GLB glTF & AR Ready GLB export',
    es: 'Exportación GLB glTF y GLB listo para RA',
    'pt-BR': 'Exportação GLB glTF e GLB pronto para RA',
    fr: 'Export GLB glTF et GLB prêt pour la RA'
  },
  featDxfExport: {
    en: 'DXF plan view export for AutoCAD & CAD tools',
    es: 'Exportación de plano DXF para AutoCAD y herramientas CAD',
    'pt-BR': 'Exportação de planta DXF para AutoCAD e ferramentas CAD',
    fr: 'Export de plan DXF pour AutoCAD et outils CAO'
  },
  featPdfDxfExport: {
    en: 'PDF & DXF plan view export',
    es: 'Exportación de planos en PDF y DXF',
    'pt-BR': 'Exportação de plantas em PDF e DXF',
    fr: 'Export de plans PDF et DXF'
  },
  featStorage5gb: {
    en: '5 GB custom model & asset storage',
    es: '5 GB de almacenamiento para modelos y recursos personalizados',
    'pt-BR': '5 GB de armazenamento para modelos e recursos personalizados',
    fr: '5 Go de stockage pour modèles et actifs personnalisés'
  },

  // UpgradeModal chrome
  unlockToolkit: {
    en: 'Unlock the full 3DStreet toolkit.',
    es: 'Desbloquea todas las herramientas de 3DStreet.',
    'pt-BR': 'Desbloqueie todas as ferramentas do 3DStreet.',
    fr: 'Débloquez tous les outils de 3DStreet.'
  },
  billingMonthly: {
    en: 'Monthly',
    es: 'Mensual',
    'pt-BR': 'Mensal',
    fr: 'Mensuel'
  },
  billingYearly: {
    en: 'Yearly',
    es: 'Anual',
    'pt-BR': 'Anual',
    fr: 'Annuel'
  },
  savePill: {
    en: 'Save 30%',
    es: 'Ahorra 30%',
    'pt-BR': 'Economize 30%',
    fr: 'Économisez 30 %'
  },
  cancelAnytime: {
    en: 'Cancel anytime',
    es: 'Cancela cuando quieras',
    'pt-BR': 'Cancele quando quiser',
    fr: 'Annulez à tout moment'
  },
  signInToUpgrade: {
    en: 'Sign in to upgrade or access Pro.',
    es: 'Inicia sesión para mejorar tu plan o acceder a Pro.',
    'pt-BR': 'Faça login para fazer upgrade ou acessar o Pro.',
    fr: 'Connectez-vous pour passer à Pro ou y accéder.'
  },
  planTokensPerMonth: {
    en: '{tokens} AI tokens / month',
    es: '{tokens} tokens de IA al mes',
    'pt-BR': '{tokens} tokens de IA por mês',
    fr: '{tokens} jetons IA par mois'
  },
  planStorage: {
    en: '{gb} GB asset storage',
    es: '{gb} GB de almacenamiento de recursos',
    'pt-BR': '{gb} GB de armazenamento de recursos',
    fr: "{gb} Go de stockage d'actifs"
  },
  // Tier names (Pro/Max) stay untranslated, so {tier} interpolates verbatim.
  goTier: {
    en: 'Go {tier}',
    es: 'Hazte {tier}',
    'pt-BR': 'Assine o {tier}',
    fr: 'Passer {tier}'
  },
  checkoutTitleUpgrade: {
    en: 'Complete your upgrade',
    es: 'Completa tu mejora',
    'pt-BR': 'Conclua seu upgrade',
    fr: 'Finalisez votre mise à niveau'
  },
  activeSubscriptionTitle: {
    en: 'Active Subscription',
    es: 'Suscripción activa',
    'pt-BR': 'Assinatura ativa',
    fr: 'Abonnement actif'
  },
  hasActiveSubscriptionHeading: {
    en: 'You Already Have an Active Subscription',
    es: 'Ya tienes una suscripción activa',
    'pt-BR': 'Você já tem uma assinatura ativa',
    fr: 'Vous avez déjà un abonnement actif'
  },
  subscriptionCountSingle: {
    en: 'You currently have 1 active subscription.',
    es: 'Actualmente tienes 1 suscripción activa.',
    'pt-BR': 'Atualmente você tem 1 assinatura ativa.',
    fr: 'Vous avez actuellement 1 abonnement actif.'
  },
  subscriptionCountMultiple: {
    en: 'You currently have {count} active subscriptions.',
    es: 'Actualmente tienes {count} suscripciones activas.',
    'pt-BR': 'Atualmente você tem {count} assinaturas ativas.',
    fr: 'Vous avez actuellement {count} abonnements actifs.'
  },
  multipleSubscriptionsNote: {
    en: 'Note: You have multiple subscriptions. Please manage them through the billing portal.',
    es: 'Nota: tienes varias suscripciones. Adminístralas desde el portal de facturación.',
    'pt-BR':
      'Observação: você tem várias assinaturas. Gerencie-as no portal de cobrança.',
    fr: 'Remarque : vous avez plusieurs abonnements. Gérez-les depuis le portail de facturation.'
  },
  billingPortalHint: {
    en: 'To add more tokens, manage your subscription, or upgrade/downgrade, please visit the billing portal.',
    es: 'Para añadir más tokens, administrar tu suscripción o cambiar de plan, visita el portal de facturación.',
    'pt-BR':
      'Para adicionar mais tokens, gerenciar sua assinatura ou mudar de plano, acesse o portal de cobrança.',
    fr: 'Pour ajouter des jetons, gérer votre abonnement ou changer de forfait, rendez-vous sur le portail de facturation.'
  },
  manageSubscription: {
    en: 'Manage Subscription',
    es: 'Administrar suscripción',
    'pt-BR': 'Gerenciar assinatura',
    fr: "Gérer l'abonnement"
  },
  welcomeToPro: {
    en: 'Welcome to Pro!',
    es: '¡Te damos la bienvenida a Pro!',
    'pt-BR': 'Boas-vindas ao Pro!',
    fr: 'Bienvenue dans Pro !'
  },
  proUnlocked: {
    en: 'Pro features are unlocked on your account.',
    es: 'Las funciones Pro están desbloqueadas en tu cuenta.',
    'pt-BR': 'Os recursos Pro estão desbloqueados na sua conta.',
    fr: 'Les fonctionnalités Pro sont débloquées sur votre compte.'
  },
  welcomeToMax: {
    en: 'Welcome to Max!',
    es: '¡Te damos la bienvenida a Max!',
    'pt-BR': 'Boas-vindas ao Max!',
    fr: 'Bienvenue dans Max !'
  },
  maxUnlocked: {
    en: 'Max features are unlocked on your account.',
    es: 'Las funciones Max están desbloqueadas en tu cuenta.',
    'pt-BR': 'Os recursos Max estão desbloqueados na sua conta.',
    fr: 'Les fonctionnalités Max sont débloquées sur votre compte.'
  },
  continueCta: {
    en: 'Continue',
    es: 'Continuar',
    'pt-BR': 'Continuar',
    fr: 'Continuer'
  },

  // EmbeddedCheckout status views
  processingPayment: {
    en: 'Processing your payment...',
    es: 'Procesando tu pago...',
    'pt-BR': 'Processando seu pagamento...',
    fr: 'Traitement de votre paiement...'
  },
  processingPaymentHint: {
    en: 'This usually takes just a few seconds',
    es: 'Esto suele tardar solo unos segundos',
    'pt-BR': 'Isso costuma levar só alguns segundos',
    fr: 'Cela ne prend généralement que quelques secondes'
  },
  almostThere: {
    en: 'Almost there!',
    es: '¡Ya casi está!',
    'pt-BR': 'Quase lá!',
    fr: 'Presque terminé !'
  },
  paymentFinalizing: {
    en: "Your payment went through and we're finalizing your account. A confirmation email is on the way. Refresh in a minute to see your updated balance.",
    es: 'Tu pago se realizó y estamos finalizando tu cuenta. Un correo de confirmación está en camino. Actualiza la página en un minuto para ver tu saldo actualizado.',
    'pt-BR':
      'Seu pagamento foi concluído e estamos finalizando sua conta. Um e-mail de confirmação está a caminho. Atualize a página em um minuto para ver seu saldo atualizado.',
    fr: 'Votre paiement a été effectué et nous finalisons votre compte. Un e-mail de confirmation est en route. Actualisez la page dans une minute pour voir votre solde à jour.'
  },
  paymentIssue: {
    en: 'Payment Issue',
    es: 'Problema con el pago',
    'pt-BR': 'Problema no pagamento',
    fr: 'Problème de paiement'
  },
  paymentErrorFallback: {
    en: 'Something went wrong with your payment. Please try again or contact support.',
    es: 'Algo salió mal con tu pago. Inténtalo de nuevo o contacta con soporte.',
    'pt-BR':
      'Algo deu errado com seu pagamento. Tente novamente ou entre em contato com o suporte.',
    fr: "Un problème est survenu avec votre paiement. Réessayez ou contactez l'assistance."
  },
  paymentSuccessTitle: {
    en: 'Payment Successful!',
    es: '¡Pago realizado!',
    'pt-BR': 'Pagamento concluído!',
    fr: 'Paiement réussi !'
  },
  paymentSuccessMessage: {
    en: 'Thanks for your purchase. Your account is ready to go.',
    es: 'Gracias por tu compra. Tu cuenta está lista.',
    'pt-BR': 'Obrigado pela sua compra. Sua conta está pronta.',
    fr: 'Merci pour votre achat. Votre compte est prêt.'
  },

  // Generator upgrade-success copy (mount-purchase-modal)
  genTokensReady: {
    en: 'Your tokens are ready — happy generating.',
    es: 'Tus tokens están listos: ¡a generar!',
    'pt-BR': 'Seus tokens estão prontos — boas gerações!',
    fr: 'Vos jetons sont prêts — bonnes générations !'
  },
  startGenerating: {
    en: 'Start Generating',
    es: 'Empezar a generar',
    'pt-BR': 'Começar a gerar',
    fr: 'Commencer à générer'
  },

  // Paywall surface registry (paywallSurfaces.jsx) — card/headline copy per
  // postCheckout surface. Format names (GLB glTF, file extensions) stay as-is.
  exportRequiresPro: {
    en: 'Export requires Pro',
    es: 'La exportación requiere Pro',
    'pt-BR': 'A exportação requer o Pro',
    fr: "L'export nécessite Pro"
  },
  surfaceExportTitle: {
    en: 'GLB glTF',
    es: 'GLB glTF',
    'pt-BR': 'GLB glTF',
    fr: 'GLB glTF'
  },
  surfaceExportSubtitle: {
    en: '.glb · 3D model export',
    es: '.glb · exportación de modelo 3D',
    'pt-BR': '.glb · exportação de modelo 3D',
    fr: '.glb · export de modèle 3D'
  },
  surfaceExportDesc: {
    en: 'GLB glTF export lets you use your 3D scene in any compatible tool, game engine, or AR platform.',
    es: 'La exportación GLB glTF te permite usar tu escena 3D en cualquier herramienta, motor de juegos o plataforma de RA compatible.',
    'pt-BR':
      'A exportação GLB glTF permite usar sua cena 3D em qualquer ferramenta, motor de jogos ou plataforma de RA compatível.',
    fr: "L'export GLB glTF vous permet d'utiliser votre scène 3D dans n'importe quel outil, moteur de jeu ou plateforme de RA compatible."
  },
  surfaceDxfTitle: {
    en: 'DXF Plan View',
    es: 'Plano DXF',
    'pt-BR': 'Planta DXF',
    fr: 'Plan DXF'
  },
  surfaceDxfSubtitle: {
    en: '.dxf · 2D CAD export',
    es: '.dxf · exportación CAD 2D',
    'pt-BR': '.dxf · exportação CAD 2D',
    fr: '.dxf · export CAO 2D'
  },
  surfaceDxfDesc: {
    en: 'DXF plan view export brings your street design into AutoCAD and other CAD tools as clean, layered 2D linework.',
    es: 'La exportación de plano DXF lleva tu diseño de calle a AutoCAD y otras herramientas CAD como líneas 2D limpias y organizadas por capas.',
    'pt-BR':
      'A exportação de planta DXF leva seu projeto de rua para o AutoCAD e outras ferramentas CAD como linhas 2D limpas e organizadas em camadas.',
    fr: "L'export de plan DXF transpose votre design de rue dans AutoCAD et d'autres outils CAO sous forme de tracés 2D propres et organisés en calques."
  },
  surfacePdfTitle: {
    en: 'PDF Plan View',
    es: 'Plano PDF',
    'pt-BR': 'Planta PDF',
    fr: 'Plan PDF'
  },
  surfacePdfSubtitle: {
    en: '.pdf · 2D plan export',
    es: '.pdf · exportación de plano 2D',
    'pt-BR': '.pdf · exportação de planta 2D',
    fr: '.pdf · export de plan 2D'
  },
  surfacePdfDesc: {
    en: 'PDF plan view export turns your street design into a print-ready vector plan you can publish and share anywhere.',
    es: 'La exportación de plano PDF convierte tu diseño de calle en un plano vectorial listo para imprimir que puedes publicar y compartir donde quieras.',
    'pt-BR':
      'A exportação de planta PDF transforma seu projeto de rua em uma planta vetorial pronta para impressão que você pode publicar e compartilhar em qualquer lugar.',
    fr: "L'export de plan PDF transforme votre design de rue en un plan vectoriel prêt à imprimer que vous pouvez publier et partager partout."
  },
  surfaceImageTitle: {
    en: 'AI Render',
    es: 'Render con IA',
    'pt-BR': 'Render com IA',
    fr: 'Rendu IA'
  },
  surfaceImageSubtitle: {
    en: 'Generation tokens · AI image rendering',
    es: 'Tokens de generación · renderizado de imágenes con IA',
    'pt-BR': 'Tokens de geração · renderização de imagens com IA',
    fr: "Jetons de génération · rendu d'images par IA"
  },
  surfaceImageHeadline: {
    en: 'More AI generation tokens',
    es: 'Más tokens de generación IA',
    'pt-BR': 'Mais tokens de geração de IA',
    fr: 'Plus de jetons de génération IA'
  },
  surfaceImageDesc: {
    en: 'AI renders use generation tokens. A paid plan refreshes your token balance every month so you can keep generating across every available AI model.',
    es: 'Los renders con IA usan tokens de generación. Un plan de pago renueva tu saldo de tokens cada mes para que sigas generando con todos los modelos de IA disponibles.',
    'pt-BR':
      'Renders com IA usam tokens de geração. Um plano pago renova seu saldo de tokens todo mês para você continuar gerando com todos os modelos de IA disponíveis.',
    fr: "Les rendus IA utilisent des jetons de génération. Un forfait payant renouvelle votre solde de jetons chaque mois pour continuer à générer avec tous les modèles d'IA disponibles."
  },
  surfaceGeoTitle: {
    en: 'Geospatial',
    es: 'Geoespacial',
    'pt-BR': 'Geoespacial',
    fr: 'Géospatial'
  },
  surfaceGeoSubtitle: {
    en: 'Real-world location · 3D map context',
    es: 'Ubicación real · contexto de mapa 3D',
    'pt-BR': 'Localização real · contexto de mapa 3D',
    fr: 'Lieu réel · contexte de carte 3D'
  },
  surfaceGeoHeadline: {
    en: 'Unlimited geospatial lookups',
    es: 'Búsquedas geoespaciales ilimitadas',
    'pt-BR': 'Consultas geoespaciais ilimitadas',
    fr: 'Recherches géospatiales illimitées'
  },
  surfaceGeoDesc: {
    en: 'Place your scene on a real-world map with 3D context of the surrounding environment. Pro unlocks unlimited location changes.',
    es: 'Sitúa tu escena en un mapa del mundo real con contexto 3D del entorno. Pro desbloquea cambios de ubicación ilimitados.',
    'pt-BR':
      'Posicione sua cena em um mapa do mundo real com contexto 3D do entorno. O Pro desbloqueia mudanças de localização ilimitadas.',
    fr: "Placez votre scène sur une carte du monde réel avec le contexte 3D de l'environnement. Pro débloque les changements de lieu illimités."
  },
  surfaceStorageTitle: {
    en: 'Cloud Storage',
    es: 'Almacenamiento en la nube',
    'pt-BR': 'Armazenamento na nuvem',
    fr: 'Stockage cloud'
  },
  surfaceStorageSubtitle: {
    en: 'Custom models & textures',
    es: 'Modelos y texturas personalizados',
    'pt-BR': 'Modelos e texturas personalizados',
    fr: 'Modèles et textures personnalisés'
  },
  surfaceStorageHeadline: {
    en: '50× more space for custom models',
    es: '50× más espacio para modelos personalizados',
    'pt-BR': '50× mais espaço para modelos personalizados',
    fr: "50× plus d'espace pour vos modèles personnalisés"
  },
  surfaceStorageDesc: {
    en: 'Your work is safe. The Free plan includes 100 MB of asset storage; Pro gives you 5 GB to grow into for custom models, textures, and splats.',
    es: 'Tu trabajo está a salvo. El plan Free incluye 100 MB de almacenamiento de recursos; Pro te da 5 GB para crecer con modelos personalizados, texturas y splats.',
    'pt-BR':
      'Seu trabalho está seguro. O plano Free inclui 100 MB de armazenamento de recursos; o Pro oferece 5 GB para crescer com modelos personalizados, texturas e splats.',
    fr: "Votre travail est en sécurité. Le forfait Free inclut 100 Mo de stockage d'actifs ; Pro vous donne 5 Go pour vos modèles personnalisés, textures et splats."
  },
  surfaceWatermarkTitle: {
    en: 'Snapshot',
    es: 'Instantánea',
    'pt-BR': 'Instantâneo',
    fr: 'Instantané'
  },
  surfaceWatermarkSubtitle: {
    en: '.jpg / .png · 2D image export',
    es: '.jpg / .png · exportación de imagen 2D',
    'pt-BR': '.jpg / .png · exportação de imagem 2D',
    fr: ".jpg / .png · export d'image 2D"
  },
  surfaceWatermarkHeadline: {
    en: 'Remove the watermark',
    es: 'Quita la marca de agua',
    'pt-BR': "Remova a marca d'água",
    fr: 'Supprimez le filigrane'
  },
  surfaceWatermarkDesc: {
    en: 'Share polished snapshots in client presentations, planning reports, and social posts.',
    es: 'Comparte instantáneas impecables en presentaciones para clientes, informes de planificación y redes sociales.',
    'pt-BR':
      'Compartilhe instantâneos impecáveis em apresentações para clientes, relatórios de planejamento e redes sociais.',
    fr: 'Partagez des instantanés soignés dans vos présentations client, rapports de planification et publications sociales.'
  },
  watermarkSecondaryCta: {
    en: 'Download now with watermark',
    es: 'Descargar ahora con marca de agua',
    'pt-BR': "Baixar agora com marca d'água",
    fr: 'Télécharger maintenant avec filigrane'
  },

  // Assets panel filter tabs
  filterAll: { en: 'All', es: 'Todos', 'pt-BR': 'Todos', fr: 'Tous' },
  filterMeshes: {
    en: 'Meshes',
    es: 'Mallas',
    'pt-BR': 'Malhas',
    fr: 'Maillages'
  },
  // "Splat" is a technical term (Gaussian splats) kept untranslated, matching
  // the editor catalogs.
  filterSplats: { en: 'Splats', es: 'Splats', 'pt-BR': 'Splats', fr: 'Splats' },
  filterImages: {
    en: 'Images',
    es: 'Imágenes',
    'pt-BR': 'Imagens',
    fr: 'Images'
  },
  filterVideo: { en: 'Video', es: 'Video', 'pt-BR': 'Vídeo', fr: 'Vidéo' },

  // Assets empty/loading states
  noAssetsYet: {
    en: 'No assets yet',
    es: 'Aún no hay recursos',
    'pt-BR': 'Ainda não há recursos',
    fr: 'Aucun actif pour le moment'
  },
  noAssetsYetUploadHint: {
    en: 'No assets yet. Drag GLB or image files in, or click Upload.',
    es: 'Aún no hay recursos. Arrastra archivos GLB o de imagen, o haz clic en Subir.',
    'pt-BR':
      'Ainda não há recursos. Arraste arquivos GLB ou de imagem, ou clique em Enviar.',
    fr: 'Aucun actif pour le moment. Glissez des fichiers GLB ou image, ou cliquez sur Téléverser.'
  },
  noMeshAssetsYet: {
    en: 'No mesh assets yet.',
    es: 'Aún no hay recursos de malla.',
    'pt-BR': 'Ainda não há recursos de malha.',
    fr: 'Aucun maillage pour le moment.'
  },
  noSplatAssetsYet: {
    en: 'No splat assets yet.',
    es: 'Aún no hay recursos de splat.',
    'pt-BR': 'Ainda não há recursos de splat.',
    fr: 'Aucun splat pour le moment.'
  },
  noImageAssetsYet: {
    en: 'No image assets yet.',
    es: 'Aún no hay recursos de imagen.',
    'pt-BR': 'Ainda não há recursos de imagem.',
    fr: 'Aucune image pour le moment.'
  },
  noVideoAssetsYet: {
    en: 'No video assets yet.',
    es: 'Aún no hay recursos de video.',
    'pt-BR': 'Ainda não há recursos de vídeo.',
    fr: 'Aucune vidéo pour le moment.'
  },
  loadingGallery: {
    en: 'Loading gallery...',
    es: 'Cargando galería...',
    'pt-BR': 'Carregando galeria...',
    fr: 'Chargement de la galerie...'
  },

  // Assets panel chrome
  signInToViewAssets: {
    en: 'Sign in to view your assets.',
    es: 'Inicia sesión para ver tus recursos.',
    'pt-BR': 'Faça login para ver seus recursos.',
    fr: 'Connectez-vous pour voir vos actifs.'
  },
  upload: {
    en: 'Upload',
    es: 'Subir',
    'pt-BR': 'Enviar',
    fr: 'Téléverser'
  },
  uploading: {
    en: 'Uploading…',
    es: 'Subiendo…',
    'pt-BR': 'Enviando…',
    fr: 'Téléversement…'
  },
  // In-flight upload stages, shared by every surface that shows one (see
  // shared/assets/uploadStageLabels.js). "Preparing" covers the quota
  // preflight: nothing is moving yet, so it must not say Uploading.
  uploadStagePreparing: {
    en: 'Preparing…',
    es: 'Preparando…',
    'pt-BR': 'Preparando…',
    fr: 'Préparation…'
  },
  uploadStageOptimizing: {
    en: 'Optimizing…',
    es: 'Optimizando…',
    'pt-BR': 'Otimizando…',
    fr: 'Optimisation…'
  },
  uploadStageUploading: {
    en: 'Uploading…',
    es: 'Subiendo…',
    'pt-BR': 'Enviando…',
    fr: 'Téléversement…'
  },
  uploadStageUploadingPct: {
    en: 'Uploading {pct}%',
    es: 'Subiendo {pct}%',
    'pt-BR': 'Enviando {pct}%',
    fr: 'Téléversement {pct}%'
  },
  uploadStageFinishing: {
    en: 'Finishing…',
    es: 'Finalizando…',
    'pt-BR': 'Finalizando…',
    fr: 'Finalisation…'
  },
  uploadAnAsset: {
    en: 'Upload an asset',
    es: 'Subir un recurso',
    'pt-BR': 'Enviar um recurso',
    fr: 'Téléverser un actif'
  },
  uploadInProgress: {
    en: 'An upload is already in progress',
    es: 'Ya hay una subida en curso',
    'pt-BR': 'Já há um envio em andamento',
    fr: 'Un téléversement est déjà en cours'
  },
  refreshAssets: {
    en: 'Refresh assets',
    es: 'Actualizar recursos',
    'pt-BR': 'Atualizar recursos',
    fr: 'Actualiser les actifs'
  },
  itemSingular: { en: 'item', es: 'elemento', 'pt-BR': 'item', fr: 'élément' },
  itemPlural: {
    en: 'items',
    es: 'elementos',
    'pt-BR': 'itens',
    fr: 'éléments'
  },

  // Bollard Buddy — AR island strings (ProfileButton sign-in prompt + gallery
  // toasts). "Bollard Buddy" is the product name and is never translated.
  bbSignInMessage: {
    en: 'Sign in to save your scenes and access your gallery.',
    es: 'Inicia sesión para guardar tus escenas y acceder a tu galería.',
    'pt-BR': 'Faça login para salvar suas cenas e acessar sua galeria.',
    fr: 'Connectez-vous pour enregistrer vos scènes et accéder à votre galerie.'
  },
  bbPhotoSaved: {
    en: 'Photo saved to gallery!',
    es: '¡Foto guardada en la galería!',
    'pt-BR': 'Foto salva na galeria!',
    fr: 'Photo enregistrée dans la galerie !'
  },
  bbPhotoSaveFailed: {
    en: 'Failed to save photo',
    es: 'No se pudo guardar la foto',
    'pt-BR': 'Falha ao salvar a foto',
    fr: "Échec de l'enregistrement de la photo"
  },
  bbGeneratorSendFailed: {
    en: 'Failed to send photo to the AI generator',
    es: 'No se pudo enviar la foto al generador de IA',
    'pt-BR': 'Falha ao enviar a foto para o gerador de IA',
    fr: "Échec de l'envoi de la photo au générateur IA"
  },

  // Bollard Buddy — static AR page UI (object picker, CTAs). Object names label
  // the placeable street furniture; "SafeHit" is a brand name kept as-is.
  bbObjectBollard: {
    en: 'Bollard',
    es: 'Bolardo',
    'pt-BR': 'Balizador',
    fr: 'Borne'
  },
  bbObjectCone: { en: 'Cone', es: 'Cono', 'pt-BR': 'Cone', fr: 'Cône' },
  bbSelectObject: {
    en: 'Select Object',
    es: 'Seleccionar objeto',
    'pt-BR': 'Selecionar objeto',
    fr: 'Sélectionner un objet'
  },
  bbTapToPlace: {
    en: 'Tap on ground to place',
    es: 'Toca el suelo para colocar',
    'pt-BR': 'Toque no chão para posicionar',
    fr: 'Touchez le sol pour placer'
  },
  bbWebxrBeta: {
    en: 'WebXR Beta',
    es: 'WebXR Beta',
    'pt-BR': 'WebXR Beta',
    fr: 'WebXR Beta'
  },
  bbIosBest: {
    en: 'Best on iOS:',
    es: 'Mejor en iOS:',
    'pt-BR': 'Melhor no iOS:',
    fr: 'Meilleur sur iOS :'
  },
  bbIosGetApp: {
    en: 'Get the app',
    es: 'Descarga la app',
    'pt-BR': 'Baixe o app',
    fr: "Télécharger l'app"
  },
  bbDismiss: {
    en: 'Dismiss',
    es: 'Descartar',
    'pt-BR': 'Dispensar',
    fr: 'Ignorer'
  },
  bbDesktopBestOniPhone: {
    en: 'Bollard Buddy is best on iPhone.',
    es: 'Bollard Buddy funciona mejor en iPhone.',
    'pt-BR': 'O Bollard Buddy funciona melhor no iPhone.',
    fr: 'Bollard Buddy est meilleur sur iPhone.'
  },
  bbGetIosApp: {
    en: 'get the iOS app',
    es: 'descarga la app de iOS',
    'pt-BR': 'baixe o app para iOS',
    fr: "télécharger l'app iOS"
  },
  bbScanQr: {
    en: 'or scan the QR code above to use the web version.',
    es: 'o escanea el código QR de arriba para usar la versión web.',
    'pt-BR': 'ou escaneie o QR code acima para usar a versão web.',
    fr: 'ou scannez le code QR ci-dessus pour utiliser la version web.'
  },

  // .gltf upload rejection (shared/asset-upload/analyzeGltf.js, #1951)
  gltfExternalRefs: {
    en: "{filename} references separate files that can't be uploaded together. Export a single .glb file instead, convert at https://gltf.report.",
    es: '{filename} hace referencia a archivos separados que no se pueden subir juntos. Exporta un solo archivo .glb en su lugar, o conviértelo en https://gltf.report.',
    'pt-BR':
      '{filename} faz referência a arquivos separados que não podem ser enviados juntos. Em vez disso, exporte um único arquivo .glb ou converta em https://gltf.report.',
    fr: '{filename} fait référence à des fichiers séparés qui ne peuvent pas être téléversés ensemble. Exportez plutôt un seul fichier .glb, ou convertissez-le sur https://gltf.report.'
  },
  gltfInvalid: {
    en: '{filename} is not a valid glTF file. Export a single .glb file and try again.',
    es: '{filename} no es un archivo glTF válido. Exporta un solo archivo .glb e inténtalo de nuevo.',
    'pt-BR':
      '{filename} não é um arquivo glTF válido. Exporte um único arquivo .glb e tente novamente.',
    fr: "{filename} n'est pas un fichier glTF valide. Exportez un seul fichier .glb et réessayez."
  },

  // Re-running the optimization pipeline on an existing asset, from the Size
  // row of the mesh details modal (shared/asset-upload/reoptimizeAsset.js).
  // Three labels for the same action, matching what that row already says:
  // optimization was skipped / there is a variant to improve on / the upload
  // predates the pipeline entirely.
  reoptimizeRetry: {
    en: 'Retry optimization',
    es: 'Reintentar optimización',
    'pt-BR': 'Tentar otimizar de novo',
    fr: "Réessayer l'optimisation"
  },
  reoptimizeAgain: {
    en: 'Reoptimize',
    es: 'Reoptimizar',
    'pt-BR': 'Reotimizar',
    fr: 'Réoptimiser'
  },
  reoptimizeStart: {
    en: 'Optimize',
    es: 'Optimizar',
    'pt-BR': 'Otimizar',
    fr: 'Optimiser'
  },
  // In-place progress on the button itself. The first stage fetches the
  // original glb, but that is an implementation detail: "Preparing" tells
  // the user what the step is for, not how it works.
  reoptimizePreparing: {
    en: 'Preparing…',
    es: 'Preparando…',
    'pt-BR': 'Preparando…',
    fr: 'Préparation…'
  },
  reoptimizeOptimizing: {
    en: 'Optimizing…',
    es: 'Optimizando…',
    'pt-BR': 'Otimizando…',
    fr: 'Optimisation…'
  },
  reoptimizeUploading: {
    en: 'Uploading…',
    es: 'Subiendo…',
    'pt-BR': 'Enviando…',
    fr: 'Téléversement…'
  },
  // Outcomes. A no-win run is reported as a message, not an error: the asset
  // is left exactly as it was.
  reoptimizeDone: {
    en: 'Reoptimized — {size} (saved {saved}).',
    es: 'Reoptimizado — {size} (ahorro de {saved}).',
    'pt-BR': 'Reotimizado — {size} (economia de {saved}).',
    fr: 'Réoptimisé — {size} ({saved} économisés).'
  },
  reoptimizeAlreadyOptimal: {
    en: 'Already optimal — the current pipeline produced nothing smaller.',
    es: 'Ya es óptimo: el proceso actual no produjo nada más pequeño.',
    'pt-BR': 'Já está ótimo — o processo atual não gerou nada menor.',
    fr: "Déjà optimal — le traitement actuel n'a rien produit de plus petit."
  },
  reoptimizeAlreadyOptimized: {
    en: 'Already optimized — this model has nothing left for the pipeline to do.',
    es: 'Ya está optimizado: el proceso no tiene nada más que hacer con este modelo.',
    'pt-BR':
      'Já otimizado — o processo não tem mais nada a fazer neste modelo.',
    fr: "Déjà optimisé — le traitement n'a plus rien à faire sur ce modèle."
  },
  reoptimizeNoChange: {
    en: 'No change.',
    es: 'Sin cambios.',
    'pt-BR': 'Sem alterações.',
    fr: 'Aucun changement.'
  },
  // The pipeline gave up rather than judged the model; the asset is unchanged.
  reoptimizeTimedOut: {
    en: 'Optimization timed out. The model is unchanged — try again on a faster device.',
    es: 'La optimización agotó el tiempo. El modelo no cambió; inténtalo en un dispositivo más rápido.',
    'pt-BR':
      'A otimização excedeu o tempo. O modelo não mudou — tente em um dispositivo mais rápido.',
    fr: "L'optimisation a expiré. Le modèle est inchangé — réessayez sur un appareil plus rapide."
  },
  reoptimizeWorkerError: {
    en: 'Optimization failed on this model. It is unchanged.',
    es: 'La optimización falló con este modelo. No cambió.',
    'pt-BR': 'A otimização falhou neste modelo. Ele não mudou.',
    fr: "L'optimisation a échoué sur ce modèle. Il est inchangé."
  },
  reoptimizeFailed: {
    en: 'Reoptimize failed',
    es: 'Error al reoptimizar',
    'pt-BR': 'Falha ao reotimizar',
    fr: 'Échec de la réoptimisation'
  },
  // Reversal of a lossy optimization: drops the optimized variant so the
  // original is served again. Sibling of the Reoptimize button.
  reoptimizeRemove: {
    en: 'Remove optimized',
    es: 'Quitar optimizado',
    'pt-BR': 'Remover otimizado',
    fr: "Retirer l'optimisé"
  },
  reoptimizeRemoved: {
    en: 'Optimized version removed. The original is served again.',
    es: 'Versión optimizada eliminada. Se vuelve a servir el original.',
    'pt-BR': 'Versão otimizada removida. O original volta a ser usado.',
    fr: "Version optimisée retirée. L'original est de nouveau servi."
  },
  reoptimizeRemoveFailed: {
    en: 'Could not remove the optimized version',
    es: 'No se pudo eliminar la versión optimizada',
    'pt-BR': 'Não foi possível remover a versão otimizada',
    fr: 'Impossible de retirer la version optimisée'
  },

  // "Copy to my library" on another user's asset in the mesh details modal
  // (shared/asset-upload/copyAsset.js): downloads the original and uploads
  // it as the viewer's own asset, so they can optimize and keep it.
  copyToLibrary: {
    en: 'Copy to my library',
    es: 'Copiar a mi biblioteca',
    'pt-BR': 'Copiar para minha biblioteca',
    fr: 'Copier dans ma bibliothèque'
  },
  copyToLibraryHint: {
    en: 'Makes your own copy: it uses your storage, gets optimized on upload, and stays even if the owner removes theirs.',
    es: 'Crea tu propia copia: usa tu almacenamiento, se optimiza al subir y se conserva aunque el propietario elimine la suya.',
    'pt-BR':
      'Cria sua própria cópia: usa seu armazenamento, é otimizada no envio e permanece mesmo se o dono remover a dele.',
    fr: "Crée votre propre copie : elle utilise votre stockage, est optimisée à l'envoi et reste même si le propriétaire supprime la sienne."
  },
  copyToLibraryCopying: {
    en: 'Copying to your library…',
    es: 'Copiando a tu biblioteca…',
    'pt-BR': 'Copiando para sua biblioteca…',
    fr: 'Copie dans votre bibliothèque…'
  },
  copyStagePreparing: {
    en: 'Preparing the original…',
    es: 'Preparando el original…',
    'pt-BR': 'Preparando o original…',
    fr: "Préparation de l'original…"
  },
  copyStageOptimizing: {
    en: 'Optimizing your copy…',
    es: 'Optimizando tu copia…',
    'pt-BR': 'Otimizando sua cópia…',
    fr: 'Optimisation de votre copie…'
  },
  copyStageUploading: {
    en: 'Uploading to your library…',
    es: 'Subiendo a tu biblioteca…',
    'pt-BR': 'Enviando para sua biblioteca…',
    fr: 'Envoi dans votre bibliothèque…'
  },
  copyStageFinishing: {
    en: 'Finishing up…',
    es: 'Finalizando…',
    'pt-BR': 'Finalizando…',
    fr: 'Finalisation…'
  },
  copyToLibraryDone: {
    en: 'Copied to your library.',
    es: 'Copiado a tu biblioteca.',
    'pt-BR': 'Copiado para sua biblioteca.',
    fr: 'Copié dans votre bibliothèque.'
  },
  // When the modal was opened from an entity: the scene now uses the copy.
  copyToLibraryDoneSwapped: {
    en: 'Copied to your library. This scene now uses your copy.',
    es: 'Copiado a tu biblioteca. Esta escena ahora usa tu copia.',
    'pt-BR': 'Copiado para sua biblioteca. Esta cena agora usa sua cópia.',
    fr: 'Copié dans votre bibliothèque. Cette scène utilise maintenant votre copie.'
  },
  copyToLibraryFailed: {
    en: 'Could not copy this asset',
    es: 'No se pudo copiar este recurso',
    'pt-BR': 'Não foi possível copiar este recurso',
    fr: 'Impossible de copier cette ressource'
  },

  // Mesh/splat details modal chrome (shared/assets/components/
  // MeshDetailsModal.jsx).
  meshPreviousItem: {
    en: 'Previous item',
    es: 'Elemento anterior',
    'pt-BR': 'Item anterior',
    fr: 'Élément précédent'
  },
  meshNextItem: {
    en: 'Next item',
    es: 'Elemento siguiente',
    'pt-BR': 'Próximo item',
    fr: 'Élément suivant'
  },
  meshTypeModel: {
    en: 'Model',
    es: 'Modelo',
    'pt-BR': 'Modelo',
    fr: 'Modèle'
  },
  // Kept untranslated: "splat" is the established term for the format.
  meshTypeSplat: {
    en: 'Splat',
    es: 'Splat',
    'pt-BR': 'Splat',
    fr: 'Splat'
  },
  meshUntitled: {
    en: 'Untitled',
    es: 'Sin título',
    'pt-BR': 'Sem título',
    fr: 'Sans titre'
  },
  meshViewerTitle: {
    en: '3D model',
    es: 'Modelo 3D',
    'pt-BR': 'Modelo 3D',
    fr: 'Modèle 3D'
  },
  meshNotAvailable: {
    en: 'Asset not available',
    es: 'Recurso no disponible',
    'pt-BR': 'Recurso não disponível',
    fr: 'Actif non disponible'
  },
  meshDeletedTitle: {
    en: 'Marked for deletion',
    es: 'Marcado para eliminación',
    'pt-BR': 'Marcado para exclusão',
    fr: 'Marqué pour suppression'
  },
  meshDeletedBody: {
    en: 'This model will be permanently purged on the next cleanup pass. Restore it to keep using it in your scenes.',
    es: 'Este modelo se eliminará definitivamente en la próxima limpieza. Restáuralo para seguir usándolo en tus escenas.',
    'pt-BR':
      'Este modelo será excluído permanentemente na próxima limpeza. Restaure-o para continuar usando nas suas cenas.',
    fr: "Ce modèle sera définitivement supprimé lors du prochain nettoyage. Restaurez-le pour continuer à l'utiliser dans vos scènes."
  },
  meshDisplayName: {
    en: 'Display name',
    es: 'Nombre visible',
    'pt-BR': 'Nome de exibição',
    fr: 'Nom affiché'
  },
  meshSaveChanges: {
    en: 'Save changes',
    es: 'Guardar cambios',
    'pt-BR': 'Salvar alterações',
    fr: 'Enregistrer les modifications'
  },
  meshSaving: {
    en: 'Saving…',
    es: 'Guardando…',
    'pt-BR': 'Salvando…',
    fr: 'Enregistrement…'
  },
  meshFieldFile: {
    en: 'File:',
    es: 'Archivo:',
    'pt-BR': 'Arquivo:',
    fr: 'Fichier :'
  },
  meshFieldSize: {
    en: 'Size:',
    es: 'Tamaño:',
    'pt-BR': 'Tamanho:',
    fr: 'Taille :'
  },
  meshFieldOptimization: {
    en: 'Optimization:',
    es: 'Optimización:',
    'pt-BR': 'Otimização:',
    fr: 'Optimisation :'
  },
  meshFieldFormat: {
    en: 'Format:',
    es: 'Formato:',
    'pt-BR': 'Formato:',
    fr: 'Format :'
  },
  meshFieldUploaded: {
    en: 'Uploaded:',
    es: 'Subido:',
    'pt-BR': 'Enviado:',
    fr: 'Téléversé :'
  },
  meshFieldAssetId: {
    en: 'Asset ID:',
    es: 'ID del recurso:',
    'pt-BR': 'ID do recurso:',
    fr: "ID de l'actif :"
  },
  meshFieldOwner: {
    en: 'Owner:',
    es: 'Propietario:',
    'pt-BR': 'Proprietário:',
    fr: 'Propriétaire :'
  },
  meshOwnerYou: {
    en: 'you',
    es: 'tú',
    'pt-BR': 'você',
    fr: 'vous'
  },
  meshOwnerOther: {
    en: 'another user',
    es: 'otro usuario',
    'pt-BR': 'outro usuário',
    fr: 'un autre utilisateur'
  },
  meshOptimizedReady: {
    en: 'Optimized variant ready',
    es: 'Variante optimizada lista',
    'pt-BR': 'Variante otimizada pronta',
    fr: 'Variante optimisée prête'
  },
  meshOptimizingJob: {
    en: 'Optimizing… ({status})',
    es: 'Optimizando… ({status})',
    'pt-BR': 'Otimizando… ({status})',
    fr: 'Optimisation… ({status})'
  },
  meshJobQueued: {
    en: 'queued',
    es: 'en cola',
    'pt-BR': 'na fila',
    fr: "en file d'attente"
  },
  meshJobRunning: {
    en: 'running',
    es: 'en curso',
    'pt-BR': 'em execução',
    fr: 'en cours'
  },
  meshJobSaving: {
    en: 'saving',
    es: 'guardando',
    'pt-BR': 'salvando',
    fr: 'enregistrement'
  },
  meshRestore: {
    en: 'Restore',
    es: 'Restaurar',
    'pt-BR': 'Restaurar',
    fr: 'Restaurer'
  },
  meshDelete: {
    en: 'Delete',
    es: 'Eliminar',
    'pt-BR': 'Excluir',
    fr: 'Supprimer'
  },
  meshDeleteConfirm: {
    en: 'Delete "{name}"?',
    es: '¿Eliminar "{name}"?',
    'pt-BR': 'Excluir "{name}"?',
    fr: 'Supprimer « {name} » ?'
  },
  meshSetThumbnail: {
    en: 'Set thumbnail from current view',
    es: 'Definir miniatura desde la vista actual',
    'pt-BR': 'Definir miniatura a partir da vista atual',
    fr: 'Définir la miniature depuis la vue actuelle'
  },
  meshThumbnailUpdated: {
    en: 'Thumbnail updated',
    es: 'Miniatura actualizada',
    'pt-BR': 'Miniatura atualizada',
    fr: 'Miniature mise à jour'
  },
  meshDownload: {
    en: 'Download',
    es: 'Descargar',
    'pt-BR': 'Baixar',
    fr: 'Télécharger'
  },
  meshDownloadOriginal: {
    en: 'Download Original',
    es: 'Descargar original',
    'pt-BR': 'Baixar original',
    fr: "Télécharger l'original"
  },
  meshDownloadOptimized: {
    en: 'Download Optimized',
    es: 'Descargar optimizado',
    'pt-BR': 'Baixar otimizado',
    fr: 'Télécharger la version optimisée'
  },
  meshPlaceInScene: {
    en: 'Place in scene',
    es: 'Colocar en la escena',
    'pt-BR': 'Colocar na cena',
    fr: 'Placer dans la scène'
  },
  meshSaveFailed: {
    en: 'Save failed',
    es: 'Error al guardar',
    'pt-BR': 'Falha ao salvar',
    fr: "Échec de l'enregistrement"
  },
  meshDeleteFailed: {
    en: 'Delete failed',
    es: 'Error al eliminar',
    'pt-BR': 'Falha ao excluir',
    fr: 'Échec de la suppression'
  },
  meshRestoreFailed: {
    en: 'Restore failed',
    es: 'Error al restaurar',
    'pt-BR': 'Falha ao restaurar',
    fr: 'Échec de la restauration'
  },
  meshCaptureFailed: {
    en: 'Could not capture the current view — wait for the splat to finish rendering, then try again.',
    es: 'No se pudo capturar la vista actual: espera a que el splat termine de renderizarse e inténtalo de nuevo.',
    'pt-BR':
      'Não foi possível capturar a vista atual — aguarde o splat terminar de renderizar e tente novamente.',
    fr: 'Impossible de capturer la vue actuelle — attendez la fin du rendu du splat, puis réessayez.'
  },
  meshRestoreQuota: {
    en: 'Not enough storage to restore ({needed} MB needed; {used} / {limit} MB used). Delete other assets or upgrade.',
    es: 'No hay espacio suficiente para restaurar ({needed} MB necesarios; {used} / {limit} MB usados). Elimina otros recursos o mejora tu plan.',
    'pt-BR':
      'Espaço insuficiente para restaurar ({needed} MB necessários; {used} / {limit} MB usados). Exclua outros recursos ou faça upgrade.',
    fr: "Stockage insuffisant pour restaurer ({needed} Mo nécessaires ; {used} / {limit} Mo utilisés). Supprimez d'autres actifs ou changez d'offre."
  },
  meshAttribution: {
    en: 'Attribution',
    es: 'Atribución',
    'pt-BR': 'Atribuição',
    fr: 'Attribution'
  },
  meshEdit: {
    en: 'Edit',
    es: 'Editar',
    'pt-BR': 'Editar',
    fr: 'Modifier'
  },
  meshNoAttribution: {
    en: 'No attribution info.',
    es: 'Sin información de atribución.',
    'pt-BR': 'Sem informações de atribuição.',
    fr: "Aucune information d'attribution."
  },
  meshNoAttributionHint: {
    en: 'Click Edit to add one.',
    es: 'Haz clic en Editar para añadir una.',
    'pt-BR': 'Clique em Editar para adicionar uma.',
    fr: 'Cliquez sur Modifier pour en ajouter une.'
  },
  meshAuthor: {
    en: 'Author',
    es: 'Autor',
    'pt-BR': 'Autor',
    fr: 'Auteur'
  },
  meshLicense: {
    en: 'License',
    es: 'Licencia',
    'pt-BR': 'Licença',
    fr: 'Licence'
  },
  meshSourceUrl: {
    en: 'Source URL',
    es: 'URL de origen',
    'pt-BR': 'URL de origem',
    fr: 'URL source'
  },
  meshLicensePlaceholder: {
    en: 'e.g. CC-BY-4.0',
    es: 'p. ej. CC-BY-4.0',
    'pt-BR': 'ex.: CC-BY-4.0',
    fr: 'p. ex. CC-BY-4.0'
  },
  meshFieldSource: {
    en: 'Source:',
    es: 'Origen:',
    'pt-BR': 'Origem:',
    fr: 'Source :'
  },
  meshFieldGenerator: {
    en: 'Generator:',
    es: 'Generador:',
    'pt-BR': 'Gerador:',
    fr: 'Générateur :'
  },
  meshViewOn: {
    en: 'View on {source}',
    es: 'Ver en {source}',
    'pt-BR': 'Ver em {source}',
    fr: 'Voir sur {source}'
  },
  meshViewSource: {
    en: 'View source',
    es: 'Ver origen',
    'pt-BR': 'Ver origem',
    fr: 'Voir la source'
  }
};

/**
 * Resolves a shared message id to the active locale's string, interpolating
 * simple {placeholder} values. Unknown ids return the id itself (loud enough
 * to spot in the UI, safe enough not to crash).
 */
export function formatSharedMessage(
  id,
  values,
  { locale = getActiveLocale() } = {}
) {
  const entry = SHARED_MESSAGES[id];
  if (!entry) return id;
  const template = entry[locale] || entry[DEFAULT_LOCALE];
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    key in values ? String(values[key]) : match
  );
}

/**
 * The editor dispatches this on its language switcher so shared components
 * (which don't subscribe to the editor's Zustand store) re-render with the new
 * language. The generator has no switcher — its locale is fixed per page load.
 */
export const LOCALE_CHANGED_EVENT = '3dstreet-locale-changed';

export function notifyLocaleChanged() {
  try {
    window.dispatchEvent(new Event(LOCALE_CHANGED_EVENT));
  } catch {
    // window unavailable (tests/SSR)
  }
}

function subscribeToLocale(callback) {
  window.addEventListener(LOCALE_CHANGED_EVENT, callback);
  // Cross-tab: another tab persisted a new locale to localStorage.
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener(LOCALE_CHANGED_EVENT, callback);
    window.removeEventListener('storage', callback);
  };
}

/**
 * The active shared locale as React state — re-renders on locale change.
 */
export function useSharedLocale() {
  return useSyncExternalStore(
    subscribeToLocale,
    getActiveLocale,
    () => DEFAULT_LOCALE
  );
}

/**
 * Returns a `t(id, values)` bound to the live locale for use in shared React
 * components.
 */
export function useSharedMessages() {
  const locale = useSharedLocale();
  return useCallback(
    (id, values) => formatSharedMessage(id, values, { locale }),
    [locale]
  );
}
